/**
 * Allegato all'offerta di un consulente, con lettura del contenuto.
 *
 * Si carica il PDF o il Word ricevuto: il sistema ne estrae il testo e
 * PROPONE i campi (numero, importo, giornate, date). I valori proposti
 * finiscono nel modulo e restano modificabili — non viene salvato nulla
 * automaticamente, perché una lettura sbagliata deve poter essere corretta
 * prima di diventare un dato contabile.
 */
import { useEffect, useState } from 'react';
import { offerteConsulentiApi, apriProtetto } from '../api';

const kb = (n) => n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`;

export default function AllegatoOfferta({ offertaId, file, onFile, onCampiLetti }) {
  const [analizzando, setAnalizzando] = useState(false);
  const [esito, setEsito] = useState(null);      // { campi, avvertenze, metodo }
  const [salvati, setSalvati] = useState([]);    // allegati già a sistema

  useEffect(() => {
    if (!offertaId) { setSalvati([]); return; }
    offerteConsulentiApi.allegati(offertaId).then(setSalvati).catch(() => setSalvati([]));
  }, [offertaId]);

  const scegli = async (f) => {
    onFile(f || null);
    setEsito(null);
    if (!f) return;
    setAnalizzando(true);
    try {
      const r = await offerteConsulentiApi.analizzaDocumento(f);
      setEsito(r.campi);
      onCampiLetti?.(r.campi);
    } catch (e) {
      setEsito({ errore: String(e) });
    } finally { setAnalizzando(false); }
  };

  const elimina = async (a) => {
    if (!confirm(`Eliminare l'allegato "${a.nome_file}"?`)) return;
    try {
      await offerteConsulentiApi.eliminaAllegato(offertaId, a.id);
      setSalvati(s => s.filter(x => x.id !== a.id));
    } catch (e) { alert(e); }
  };

  return (
    <div className="bg-gray-50 border rounded-lg p-3 space-y-2">
      <div>
        <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          Documento del consulente
        </div>
        <p className="text-xs text-gray-500 mt-0.5">
          Carica il PDF o il Word ricevuto: il sistema ne legge il contenuto e propone
          i campi qui sotto. Puoi sempre correggerli prima di salvare.
        </p>
      </div>

      {/* Allegati già salvati */}
      {salvati.map(a => (
        <div key={a.id} className="flex items-center gap-2 bg-white border rounded px-2 py-1.5 text-sm">
          {/* Non un <a href>: il documento è protetto e va chiesto con il
              token, altrimenti il server risponde 401 e non si apre nulla. */}
          <button className="text-brand-600 hover:underline flex-1 truncate text-left"
                  onClick={() => apriProtetto(`/offerte-consulenti/${offertaId}/allegati/${a.id}`, a.nome_file)
                    .catch(e => alert(e))}>
            📎 {a.nome_file}
          </button>
          <span className="text-xs text-gray-400 shrink-0">{kb(a.dimensione || 0)}</span>
          {!a.ha_testo && (
            <span className="text-xs text-amber-600 shrink-0" title="Testo non estraibile: probabilmente una scansione">
              non letto
            </span>
          )}
          <button type="button" onClick={() => elimina(a)}
                  className="text-gray-400 hover:text-red-600 px-1" title="Elimina allegato">✕</button>
        </div>
      ))}

      <div className="flex items-center gap-2">
        <input type="file" className="text-xs flex-1" accept=".pdf,.doc,.docx,.txt"
               onChange={e => scegli(e.target.files?.[0] || null)} />
        {analizzando && <span className="text-xs text-gray-500 shrink-0">lettura in corso…</span>}
      </div>

      {file && !analizzando && (
        <div className="text-xs text-gray-500">
          Da allegare: <strong>{file.name}</strong> ({kb(file.size)}) — verrà salvato con l'offerta.
        </div>
      )}

      {/* Esito della lettura */}
      {esito?.errore && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          {esito.errore}
        </div>
      )}
      {esito && !esito.errore && (
        <div className="text-xs bg-white border rounded p-2 space-y-1">
          <div className="font-medium text-gray-700">
            Campi proposti dal documento
            <span className="ml-1 font-normal text-gray-400">
              ({esito.metodo === 'assistita' ? 'lettura assistita' : 'lettura di base'} · affidabilità {esito.confidenza})
            </span>
          </div>
          <div className="text-gray-600">
            {[['n.', esito.numero], ['importo', esito.importo_max ? `${esito.importo_max} €` : null],
              ['giornate', esito.giornate], ['data', esito.data_offerta]]
              .filter(([, v]) => v != null && v !== '')
              .map(([k, v]) => `${k} ${v}`).join('  ·  ') || 'nessun campo riconosciuto'}
          </div>
          {(esito.avvertenze || []).length > 0 && (
            <ul className="text-amber-700 list-disc list-inside">
              {esito.avvertenze.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
